/**
 * Mic capture → Float32 mono PCM at 16 kHz for Whisper.
 */

function getWin(): any {
  const g: any = typeof global !== 'undefined' ? global : {};
  return g.window || g;
}

function downsample(input: Float32Array, inRate: number, outRate: number): Float32Array {
  if (inRate === outRate) return input;
  if (outRate > inRate) {
    // rare — just return as-is; whisper prefers 16k
    return input;
  }
  const ratio = inRate / outRate;
  const newLen = Math.max(1, Math.floor(input.length / ratio));
  const out = new Float32Array(newLen);
  for (let i = 0; i < newLen; i++) {
    const start = Math.floor(i * ratio);
    const end = Math.min(Math.floor((i + 1) * ratio), input.length);
    let sum = 0;
    let count = 0;
    for (let j = start; j < end; j++) {
      sum += input[j];
      count++;
    }
    out[i] = count ? sum / count : input[start] || 0;
  }
  return out;
}

/**
 * Encode mono Float32 PCM ([-1, 1]) as a 16-bit WAV blob for <audio> playback.
 */
export function encodeWav(pcm: Float32Array, sampleRate: number = 16000): Blob {
  const numChannels = 1;
  const bitsPerSample = 16;
  const bytesPerSample = bitsPerSample / 8;
  const blockAlign = numChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = pcm.length * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const writeStr = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);
  writeStr(36, 'data');
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let i = 0; i < pcm.length; i++) {
    const s = Math.max(-1, Math.min(1, pcm[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }

  const win = getWin();
  const BlobCtor = win.Blob || (global as any).Blob;
  return new BlobCtor([buffer], { type: 'audio/wav' });
}

export type MicRecorder = {
  start: () => Promise<void>;
  stop: () => Promise<Float32Array>;
  isRecording: () => boolean;
};

/**
 * Create a mic recorder that captures mono PCM and returns 16 kHz Float32 on stop.
 */
export function createMicRecorder(maxSeconds: number = 15): MicRecorder {
  const win = getWin();
  let stream: any = null;
  let audioCtx: any = null;
  let processor: any = null;
  let source: any = null;
  let chunks: Float32Array[] = [];
  let recording = false;
  let stopTimer: any = null;
  let sampleRate = 16000;

  const cleanup = () => {
    if (stopTimer) {
      clearTimeout(stopTimer);
      stopTimer = null;
    }
    try {
      if (processor) {
        processor.onaudioprocess = null;
        processor.disconnect();
      }
    } catch {
      /* ignore */
    }
    try {
      if (source) source.disconnect();
    } catch {
      /* ignore */
    }
    try {
      if (audioCtx && audioCtx.state !== 'closed') audioCtx.close();
    } catch {
      /* ignore */
    }
    if (stream) {
      stream.getTracks().forEach((t: any) => {
        try {
          t.stop();
        } catch {
          /* ignore */
        }
      });
    }
    processor = null;
    source = null;
    audioCtx = null;
    stream = null;
    recording = false;
  };

  return {
    isRecording: () => recording,

    start: async () => {
      if (recording) return;
      chunks = [];
      const nav = win.navigator;
      if (!nav || !nav.mediaDevices || !nav.mediaDevices.getUserMedia) {
        throw new Error('Trình duyệt không hỗ trợ micro. Hãy dùng Chrome/Edge.');
      }
      try {
        stream = await nav.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            channelCount: 1,
          },
        });
      } catch (err: any) {
        const name = err && err.name ? String(err.name) : '';
        if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
          throw new Error('Bạn đã chặn quyền micro. Hãy mở cài đặt trang và cho phép micro.');
        }
        throw new Error((err && err.message) || 'Không mở được micro.');
      }

      const AC = win.AudioContext || win.webkitAudioContext;
      audioCtx = new AC({ sampleRate: 16000 });
      sampleRate = audioCtx.sampleRate || 16000;
      source = audioCtx.createMediaStreamSource(stream);
      // ScriptProcessor is deprecated but works everywhere (CRA / older browsers)
      const bufferSize = 4096;
      processor = audioCtx.createScriptProcessor(bufferSize, 1, 1);
      processor.onaudioprocess = (e: any) => {
        if (!recording) return;
        const input = e.inputBuffer.getChannelData(0);
        chunks.push(new Float32Array(input));
      };
      source.connect(processor);
      // Mute output to avoid feedback while keeping processor in the graph
      const mute = audioCtx.createGain();
      mute.gain.value = 0;
      processor.connect(mute);
      mute.connect(audioCtx.destination);
      recording = true;

      // Cap collection length; UI also stops via timer
      stopTimer = setTimeout(() => {
        recording = false;
      }, maxSeconds * 1000);
    },

    stop: async () => {
      const wasRecording = recording || chunks.length > 0;
      recording = false;
      if (!wasRecording && !chunks.length) {
        cleanup();
        return new Float32Array(0);
      }

      // flush a tiny delay so last buffer arrives
      await new Promise((r) => setTimeout(r, 50));

      let total = 0;
      chunks.forEach((c) => {
        total += c.length;
      });
      const merged = new Float32Array(total);
      let offset = 0;
      chunks.forEach((c) => {
        merged.set(c, offset);
        offset += c.length;
      });
      cleanup();
      chunks = [];
      return downsample(merged, sampleRate, 16000);
    },
  };
}
